import { useState, type ReactNode } from "react";
import { COLORS } from "../../lib/brand";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  View,
  type GestureResponderEvent,
  type ViewStyle,
} from "react-native";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "ghost-light"
  | "ghost-dark"
  | "cta"
  | "link";
type ButtonSize = "sm" | "md" | "lg";

const FOREST = COLORS.FOREST.hex;
const AMBER = COLORS.AMBER.hex;
const AMBER_WARM = COLORS.AMBER_WARM.hex;
const LINEN = COLORS.LINEN.hex;
const SAND_DARK = "#D9D2C5";

/**
 * Button — a unified pressable for the whole app.
 *
 * Variants:
 *   - primary       Solid forest, amber on hover. Use for the main action on a page.
 *   - secondary     Solid amber on a light surface. High emphasis without going dark.
 *   - cta           Two-tone amber + forest with chevron. Marketing / hero CTAs.
 *   - ghost-light   Outlined neutral on a light surface. Restrained secondary action.
 *   - ghost-dark    Outlined neutral on a dark surface (forest hero).
 *   - link          Bare text link, amber. Use for ← Back, ← All programs, etc.
 *
 * Premium micro-interactions baked in:
 *   - Web hover: bg/border tint shifts toward amber over 180ms
 *   - Press: gentle scale 0.97 + opacity dip — fast tactile feedback, no jitter
 *   - Disabled: 55% opacity, no hover
 */
export function Button({
  variant = "primary",
  size = "md",
  disabled,
  loading,
  onPress,
  children,
  leftIcon,
  rightIcon,
  theme = "light",
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  onPress?: (event: GestureResponderEvent) => void;
  children: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  theme?: "light" | "dark";
}) {
  const [hovered, setHovered] = useState(false);
  const v: ButtonVariant =
    variant === "primary" || variant === "secondary" || variant === "cta" || variant === "link"
      ? variant
      : variant === "ghost-light" || variant === "ghost-dark"
        ? variant
        : theme === "dark"
          ? "ghost-dark"
          : "ghost-light";

  const isDisabled = !!disabled || !!loading;
  const web = Platform.OS === "web";
  const isWebHover = web && hovered && !isDisabled;

  const webHoverProps = web
    ? ({
        onHoverIn: () => setHovered(true),
        onHoverOut: () => setHovered(false),
      } as const)
    : {};

  // ── LINK variant: bare amber text link with hover underline. ──
  if (v === "link") {
    return (
      <Pressable
        {...webHoverProps}
        disabled={isDisabled}
        onPress={onPress}
        accessibilityRole="link"
        style={[
          { paddingVertical: 4, paddingHorizontal: 0, alignSelf: "flex-start" },
          web ? ({ cursor: isDisabled ? "default" : "pointer" } as ViewStyle) : null,
          isDisabled ? { opacity: 0.5 } : null,
        ]}
      >
        <View className="flex-row items-center gap-1.5">
          {leftIcon}
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: size === "sm" ? 10.5 : 11.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: isWebHover ? AMBER_WARM : AMBER,
              ...(web
                ? ({
                    transition: "color 180ms ease",
                    textDecorationLine: isWebHover ? "underline" : "none",
                    textUnderlineOffset: 3,
                  } as object)
                : {}),
            }}
          >
            {children}
          </Text>
          {rightIcon}
        </View>
      </Pressable>
    );
  }

  // ── CTA variant: amber + forest split with chevron. ──
  if (v === "cta") {
    return (
      <Pressable
        {...webHoverProps}
        disabled={isDisabled}
        onPress={onPress}
        style={({ pressed }) => [
          {
            opacity: isDisabled ? 0.55 : 1,
            transform: [{ scale: pressed && !isDisabled ? 0.97 : 1 }],
          },
          web
            ? ({
                cursor: isDisabled ? "default" : "pointer",
                transition: "transform 120ms ease",
              } as object)
            : null,
        ]}
      >
        <View className="flex-row overflow-hidden rounded-[3px] border border-transparent">
          <View
            style={{
              backgroundColor: isWebHover ? AMBER_WARM : AMBER,
              paddingHorizontal: size === "lg" ? 28 : 20,
              paddingVertical: size === "lg" ? 16 : 12,
              ...(web ? ({ transition: "background-color 180ms ease" } as object) : {}),
            }}
          >
            {loading ? (
              <ActivityIndicator color={FOREST} />
            ) : (
              <Text
                style={{
                  fontFamily: "DMSans_700Bold",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 1.6,
                  color: FOREST,
                }}
              >
                {children}
              </Text>
            )}
          </View>
          <View
            style={{
              backgroundColor: COLORS.FOREST_MID.hex,
              paddingHorizontal: size === "lg" ? 24 : 20,
              paddingVertical: size === "lg" ? 16 : 12,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {rightIcon ?? <Text style={{ color: AMBER, fontSize: 14 }}>→</Text>}
          </View>
        </View>
      </Pressable>
    );
  }

  // ── Standard variants (primary / secondary / ghost-light / ghost-dark). ──
  const sizeStyles =
    size === "sm"
      ? { paddingHorizontal: 12, paddingVertical: 7, fontSize: 10.5 }
      : size === "lg"
        ? { paddingHorizontal: 22, paddingVertical: 14, fontSize: 12.5 }
        : { paddingHorizontal: 16, paddingVertical: 11, fontSize: 11.5 };

  let bg: string;
  let border: string;
  let textColor: string;
  let hoverBg: string;
  let hoverBorder: string;
  let hoverText: string;

  if (v === "primary") {
    bg = FOREST;
    border = FOREST;
    textColor = LINEN;
    hoverBg = COLORS.FOREST_MID.hex;
    hoverBorder = AMBER;
    hoverText = LINEN;
  } else if (v === "secondary") {
    bg = AMBER;
    border = AMBER;
    textColor = FOREST;
    hoverBg = AMBER_WARM;
    hoverBorder = AMBER_WARM;
    hoverText = FOREST;
  } else if (v === "ghost-dark") {
    bg = "rgba(255,255,255,0.06)";
    border = "rgba(255,255,255,0.18)";
    textColor = LINEN;
    hoverBg = "rgba(181,135,58,0.12)";
    hoverBorder = "rgba(181,135,58,0.55)";
    hoverText = AMBER;
  } else {
    // ghost-light
    bg = "rgba(255,255,255,0.55)";
    border = SAND_DARK;
    textColor = FOREST;
    hoverBg = "rgba(181,135,58,0.08)";
    hoverBorder = AMBER;
    hoverText = FOREST;
  }

  const activeBg = isWebHover ? hoverBg : bg;
  const activeBorder = isWebHover ? hoverBorder : border;
  const activeText = isWebHover ? hoverText : textColor;

  return (
    <Pressable
      {...webHoverProps}
      disabled={isDisabled}
      onPress={onPress}
      style={({ pressed }) => [
        {
          backgroundColor: activeBg,
          borderColor: activeBorder,
          borderWidth: 1,
          borderRadius: 6,
          paddingHorizontal: sizeStyles.paddingHorizontal,
          paddingVertical: sizeStyles.paddingVertical,
          opacity: isDisabled ? 0.55 : 1,
          transform: [{ scale: pressed && !isDisabled ? 0.97 : 1 }],
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        },
        web
          ? ({
              cursor: isDisabled ? "default" : "pointer",
              transition: "background-color 180ms ease, border-color 180ms ease, transform 120ms ease",
            } as object)
          : null,
      ]}
    >
      {loading ? (
        <ActivityIndicator color={v === "secondary" ? FOREST : v === "primary" ? LINEN : activeText} />
      ) : (
        <>
          {leftIcon}
          <Text
            style={{
              fontFamily: "DMSans_700Bold",
              fontSize: sizeStyles.fontSize,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: activeText,
              ...(web ? ({ transition: "color 180ms ease" } as object) : {}),
            }}
          >
            {children}
          </Text>
          {rightIcon}
        </>
      )}
    </Pressable>
  );
}
